import { Box, Typography, Grid, IconButton, Stack, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { ReactComponent as ExpandIcon } from '../../../assets/PepWise2.0/Expand.svg';
import { ReactComponent as CollapseIcon } from '../../../assets/PepWise2.0/Collapse.svg';
import { ReactComponent as CollapseBlackIcon } from '../../../assets/PepWise2.0/collapseBlack.svg';
import { ReactComponent as ExportIcon } from '../../../assets/PepWise2.0/export.svg';
import { ReactComponent as ShareIcon } from '../../../assets/PepWise2.0/share.svg';
import React, { useState, useMemo } from 'react';
import WidgetWraper from 'components/Common/WidgetWraper';
import BasicPiechart from 'components/Widgets/BasicPiechart';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { InfoTooltip } from '../InfoTooltip';
import { useTheme } from '@mui/material/styles';
import UnitToggle, { StorageUnit } from 'components/Common/UnitToggle';
import BasicDonutchart from '../BasicDonutChart';

// Icons from @mui/icons-material
import MoreVertIcon from '@mui/icons-material/MoreVert';
interface ChartConfig {
  chartType: 'pie' | 'donut';
  title?: string;
  data: { name: string; value: number }[];
  colorMapping?: any;
  onSliceClick?: (item: any) => void;
}

interface TabConfig {
  key: string;
  label: string;
  showUnitToggle: boolean;
  tooltip?: string | JSX.Element;
  charts: ChartConfig[];
}

interface DashboardCardProps {
  title: string;
  hasTooltip?: boolean;
  tooltipLabel?:
    | string
    | JSX.Element
    | {
        title: string;
        description: string | React.ReactNode;
      }[];
  tabs: TabConfig[];
  showUnits?: boolean;
  isExpanded?: boolean;
  isMaximized?: boolean;
  exportCallback?: any;
  onToggleExpand?: () => void;
}

type ExtraOptionsProps = {
  colors: any;
  exportCallback?: () => void;
  onToggleExpand?: () => void;
};

const ExtraOptions = ({
  colors,
  exportCallback,
  onToggleExpand
}: ExtraOptionsProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVertIcon sx={{ color: colors.widget.filter.textPrimary }} />
      </IconButton>

      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              width: '240px',
              backgroundColor: colors.kpiTile.background.card,
              borderRadius: '8px',
              boxShadow: '0px 4px 20px rgba(0,0,0,0.1)',
            },
          },
        }}
        sx={{
          '& .MuiMenu-paper': {
            backgroundColor: `${colors.kpiTile.background.card} !important`,
          },
        }}
      >
        <MenuItem onClick={exportCallback} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <ExportIcon />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: colors.kpiTile.text.main, fontWeight: 600 }}>
            Export as CSV
          </ListItemText>
        </MenuItem>

        {/* <MenuItem onClick={handleClose} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <ImageIcon />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: colors.kpiTile.text.main, fontWeight: 600 }}>
            Export as PNG
          </ListItemText>
        </MenuItem> */}

        <Divider />

        <MenuItem onClick={handleClose} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <ShareIcon />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: colors.kpiTile.text.main, fontWeight: 600 }}>
            Share
          </ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={onToggleExpand} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <CollapseBlackIcon />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: colors.kpiTile.text.main, fontWeight: 600 }}>
            Exit Full Screen
          </ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};

const MAXIMIZED_SIZE = '600px'
const NORMAL_SIZE = '200px'

export default function DashboardCard({
  title,
  tooltipLabel,
  hasTooltip = true,
  tabs,
  showUnits = true,
  isExpanded = false,
  isMaximized= false,
  exportCallback,
  onToggleExpand,
}: DashboardCardProps) {
  const theme = useTheme();
  const colors = theme.palette;

  const [activeTab, setActiveTab] = useState(tabs[0].key);
  const baseUnit: StorageUnit = 'GB';
  const [selectedUnit, setSelectedUnit] = useState<StorageUnit>('GB');
  const active = tabs.find((t) => t.key === activeTab);

  function convertUnit(
    value: number,
    fromUnit: 'PB' | 'TB' | 'GB',
    toUnit: 'PB' | 'TB' | 'GB',
    si = false,
    precision = 2,
  ) {
    if (!value) return 0;

    if (!active?.showUnitToggle) return value;

    const factor = si ? 1000 : 1024;

    const unitMap = {
      PB: factor ** 2,
      TB: factor ** 1,
      GB: factor ** 0,
    };

    const base = value * unitMap[fromUnit];
    const result = base / unitMap[toUnit];

    return Number(result.toFixed(precision));
  }

  const convertedCharts = useMemo(() => {
    if (!active?.charts) return [];

    return active.charts.map((chart) => ({
      ...chart,
      data: chart.data.map((d) => ({
        ...d,
        value: convertUnit(d.value, baseUnit, selectedUnit, false, 2),
      })),
    }));
  }, [active?.charts, activeTab, selectedUnit]);

  const renderChart = (chart: ChartConfig) => {
    const events = {
      click: (params: any) => {
        const found = chart.data.find((d) => d.name === params.name);
        chart.onSliceClick?.(found);
      },
    };

    if (chart.chartType === 'pie') {
      return (
        <BasicPiechart
          isLoading={false}
          seriesData={chart.data}
          colorMapping={chart.colorMapping}
          onSliceClick={(e: any) => events.click(e)}
          isExpanded={isExpanded}
          unit={selectedUnit}
          customSize={isMaximized ? MAXIMIZED_SIZE : NORMAL_SIZE }
        />
      );
    }

    if (chart.chartType === 'donut') {
      return (
        <BasicDonutchart
          title={chart.title || ''}
          isLoading={false}
          seriesData={chart.data}
          colorMapping={chart.colorMapping}
          onSliceClick={(e: any) => events.click(e)}
          isExpanded={isExpanded}
          unit={selectedUnit}
          customSize={isMaximized ? MAXIMIZED_SIZE : NORMAL_SIZE }
        />
      );
    }

    return null;
  };

  const getGridSizes = (count: number) => {
    if (count === 1) return { xs: 12, sm: 12, md: 12, lg: 12 };
    if (count === 2) return { xs: 12, sm: 6, md: 6, lg: 6 };
    if (count === 3) return { xs: 12, sm: 6, md: 4, lg: 4 };
    return { xs: 12, sm: 6, md: 4, lg: 3 };
  };

  return (
    <WidgetWraper>
      <Box
        sx={{
          backgroundColor: `${colors.widget.background.card} !important`,
          display: 'flex',
          flexDirection: 'Column',
          color: `${colors.widget.text.main} !important`,
          position: 'relative', // anchor the button
          transition: 'height 300ms ease, width 300ms ease',
          willChange: 'height, width',
          overflow: 'hidden',
          minHeight: isMaximized ? MAXIMIZED_SIZE : NORMAL_SIZE,
        }}
      >
        {/* HEADER */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center">
            <Typography
              variant="widgetTitle"
              sx={{ color: `${colors.widget.text.title} !important` }}
            >
              <HighlightedText text={title} />
            </Typography>
            {hasTooltip && !!tooltipLabel && <InfoTooltip label={tooltipLabel} />}
          </Stack>
          {
            !!isMaximized && 
            <>
              <ExtraOptions
                colors={colors}
                exportCallback={exportCallback}
                onToggleExpand={onToggleExpand}
              />
            </>
          }
          
        </Stack>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 1,
            width: '100%',
          }}
        >
          {/* CUSTOM SCROLLABLE TABS */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              overflowX: 'auto',
              whiteSpace: 'nowrap',
              borderBottom: `1px solid ${colors.widget.tab.activeBorderColor}`,
              mt: 2,
              mb: 1,
            }}
          >
            {tabs.map((tab) => (
              <Box
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  color: activeTab === tab.key ? `${colors.widget.tab.active.color}` : `${colors.widget.tab.inActive.color}`,
                  borderBottom: activeTab === tab.key ? `4px solid ${colors.widget.tab.activeBorderColor}` : `${colors.widget.tab.inActiveBorderColor}`,
                }}
              >
                <Typography
                  variant="tabLabel"
                  sx={{
                    paddingBottom: '4px',
                    fontWeight: 500,
                  }}
                >
                  {tab.label}
                </Typography>

                {/* Tooltip icon next to each tab */}
                {tab.tooltip && <InfoTooltip label={tab.tooltip} />}
              </Box>
            ))}
          </Box>

          {active?.showUnitToggle && (
            <UnitToggle
              units={['PB', 'TB', 'GB']}
              baseUnit="GB"
              precision={2}
              si={false}
              onUnitChange={(u) => setSelectedUnit(u)}
            />
          )}
        </Box>

        {/* CHARTS */}
        {/* <Grid container spacing={1} sx={{ height: isExpanded ? 320 : 200, width: '100%' }}>
          {convertedCharts?.map((chart, idx) => (
            <Grid item xs={12} md={4} lg={12} key={idx} sx={{ height: '100%', width: '100%', p: 0 }}>
              <Box sx={{ height: '100%', width: '100%' }}>{renderChart(chart)}</Box>
            </Grid>
          ))}
        </Grid> */}
        <Grid container spacing={1} sx={{  width: '100%', minHeight: isMaximized ? MAXIMIZED_SIZE : NORMAL_SIZE, }}>
          {convertedCharts.map((chart) => {
            const grid = getGridSizes(convertedCharts.length);
            const chartKey = chart.data.map((d) => d.name).join('-');

            return (
              <Grid
                item
                key={chartKey}
                xs={grid.xs}
                sm={grid.sm}
                md={grid.md}
                lg={grid.lg}
                sx={{
                  minWidth: 300,
                  minHeight: isMaximized ? MAXIMIZED_SIZE : NORMAL_SIZE,
                }}
              >
                {renderChart(chart)}
              </Grid>
            );
          })}
        </Grid>

        {/* EXPAND / COLLAPSE BUTTON */}
        {onToggleExpand && (
          <IconButton
            aria-label={isMaximized ? 'Collapse' : 'Expand'}
            size="small"
            onClick={onToggleExpand}
            sx={{
              position: 'absolute',
              right: 3,
              bottom: 0,
              zIndex: 2,
            }}
          >
            {isMaximized ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        )}
      </Box>
    </WidgetWraper>
  );
}
